import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useWorkout } from '../context/WorkoutContext';
import './RepCountScreen.css';

const RepCountScreen = () => {
    const navigate = useNavigate();
    const { workoutCode } = useParams();
    const location = useLocation();
    const { state, actions, computed } = useWorkout();
    const [repCount, setRepCount] = useState(state.repCount || 10);

    // Determine workout type from the current route
    const getWorkoutTypeFromRoute = () => {
        const path = location.pathname;
        if (path.includes('bodyweight-rep-count')) return 'bodyweight';
        if (path.includes('bands-rep-count')) return 'bands';
        if (path.includes('strength-rep-count')) return 'strength';
        if (path.includes('advanced-bodyweight-rep-count')) return 'advanced-bodyweight';
        if (path.includes('everything-rep-count')) return 'everything';
        if (path.includes('battle-rep-count')) return 'battle';
        return 'custom';
    };

    const workoutType = getWorkoutTypeFromRoute();

    // Decode the workout code if it came from URL params
    const decodedWorkoutCode = workoutCode ? decodeURIComponent(workoutCode) : state.selectedMuscleGroups || '';

    // Update the rep count when it changes
    useEffect(() => {
        actions.setRepCount(repCount);
    }, [repCount, actions]);

    // Handle rep count change
    const handleRepCountChange = (e) => {
        const value = parseInt(e.target.value);
        if (!isNaN(value) && value >= 1) {
            setRepCount(value);
        }
    };

    // Handle increment/decrement of rep count
    const incrementReps = () => setRepCount(prev => prev + 1);
    const decrementReps = () => setRepCount(prev => prev > 1 ? prev - 1 : 1);

    // Handle next button click
    const handleNext = () => {
        // Set the rep count in the workout context
        actions.setRepCount(repCount);
        actions.setSetCount(1); // Default to 1 set

        // Generate the workout based on the decoded workout code
        if (actions.generateWorkoutWithCode) {
            actions.generateWorkoutWithCode(decodedWorkoutCode);
        } else {
            // Fallback: try to generate workout with the available method
            actions.generateWorkout();
        }

        // Navigate to the workout summary screen with proper state
        navigate('/workout-summary', {
            state: {
                workoutCode: decodedWorkoutCode,
                workoutType: workoutType,
                repCount: repCount,
                setCount: 1,
                selectedMuscleGroups: decodedWorkoutCode // Pass as muscle groups too
            }
        });
    };

    // Handle back navigation
    const handleBack = () => {
        // Navigate back to the appropriate selection screen
        switch (workoutType) {
            case 'bodyweight':
                navigate('/bodyweight-selection');
                break;
            case 'bands':
                navigate('/bands-selection');
                break;
            case 'strength':
                navigate('/strength-selection');
                break;
            case 'advanced-bodyweight':
                navigate('/advanced-bodyweight-selection');
                break;
            case 'everything':
                navigate('/everything-selection');
                break;
            case 'battle':
                navigate('/battle-selection');
                break;
            default:
                navigate(-1); // Fallback to previous page
        }
    };

    return (
        <div className="rep-count-container">
            <header className="rep-count-header">
                <button className="back-button" onClick={handleBack}>
                    Back
                </button>
                <h1>Set Rep Count</h1>
            </header>

            <div className="rep-count-section">
                <h2>How many reps would you like to do per exercise?</h2>

                <div className="rep-counter">
                    <button
                        className="counter-button"
                        onClick={decrementReps}
                        disabled={repCount <= 1}
                    >
                        -
                    </button>

                    <input
                        type="number"
                        value={repCount}
                        onChange={handleRepCountChange}
                        min="1"
                        className="rep-input"
                    />

                    <button
                        className="counter-button"
                        onClick={incrementReps}
                    >
                        +
                    </button>
                </div>

                <div className="rep-level">
                    <h3>Rep Level:</h3>
                    <p className="level-indicator">{computed.repCountLevel}</p>
                </div>
            </div>

            <div className="rep-explanation">
                <h3>What do these rep ranges mean?</h3>
                <ul>
                    <li><strong>1-7 reps:</strong> Max strength focus</li>
                    <li><strong>8-17 reps:</strong> Challenge level</li>
                    <li><strong>18-26 reps:</strong> Strength with endurance</li>
                    <li><strong>27-35 reps:</strong> Ideal strength/endurance ratio</li>
                    <li><strong>36-43 reps:</strong> Max endurance focus</li>
                </ul>
            </div>

            <button
                className="next-button"
                onClick={handleNext}
            >
                Next
            </button>
        </div>
    );
};

export default RepCountScreen;